import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBackRounded, PlayCircleOutlineRounded, SaveRounded } from '@mui/icons-material';

const getThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
const getPreviewLink = (videoId) => `https://www.youtube.com/embed/${videoId}?&controls=1&mute=0&disablekb=1`;

const AddEditPartner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState({
    name: '',
    brief: '',
    promotion_order: '',
    video_link: ''
  });

  useEffect(() => {
    if (id) {
      axios.get(`https://aaryanholidays.in/backend/partners/${id}`)
        .then(response => setPartner(response.data))
        .catch(error => console.error('Error fetching partner:', error));
    }
  }, [id]);

  const previewReady = useMemo(() => partner.video_link && partner.video_link.trim().length > 0, [partner.video_link]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPartner({
      ...partner,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const request = id
      ? axios.put(`https://aaryanholidays.in/backend/partners/${id}`, partner)
      : axios.post('https://aaryanholidays.in/backend/partners', partner);

    request
      .then(() => navigate('/manage-partners'))
      .catch(error => console.error('Error saving data:', error));
  };

  return (
    <Box>
      <Card
        sx={{
          mb: 3,
          borderRadius: 5,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.75 }}>
                {id ? 'Edit Partner' : 'Add New Partner'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 760 }}>
                Shape how each premium partner appears on the homepage with cleaner copy, stronger ordering, and instant video preview.
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/manage-partners')}
              startIcon={<ArrowBackRounded />}
              variant="outlined"
              sx={{
                borderRadius: 999,
                px: 2.5,
                py: 1.15,
                textTransform: 'none',
                fontWeight: 700
              }}
            >
              Back to Partners
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              borderRadius: 5,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Partner Name"
                      name="name"
                      value={partner.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Brief"
                      name="brief"
                      value={partner.brief}
                      onChange={handleChange}
                      required
                      multiline
                      minRows={4}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Promotion Order"
                      name="promotion_order"
                      value={partner.promotion_order}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="YouTube Video ID"
                      name="video_link"
                      value={partner.video_link}
                      onChange={handleChange}
                      required
                      helperText="Example: use the video ID, not the full YouTube URL."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      startIcon={<SaveRounded />}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 700,
                        boxShadow: '0 18px 36px rgba(37, 99, 235, 0.24)'
                      }}
                    >
                      {id ? 'Save Partner' : 'Create Partner'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 5,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
              height: '100%'
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                Live Preview
              </Typography>

              {previewReady ? (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      borderRadius: 4,
                      border: '1px solid rgba(148, 163, 184, 0.18)'
                    }}
                  >
                    <Box
                      component="img"
                      src={getThumbnail(partner.video_link)}
                      alt={partner.name || 'Partner preview'}
                      sx={{
                        width: '100%',
                        display: 'block',
                        aspectRatio: '16 / 9',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {partner.name || 'Partner title'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.7 }}>
                    {partner.brief || 'Partner description will appear here as you type.'}
                  </Typography>

                  <Button
                    component="a"
                    href={getPreviewLink(partner.video_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<PlayCircleOutlineRounded />}
                    variant="outlined"
                    sx={{
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    Open Video Preview
                  </Button>
                </Stack>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    backgroundColor: '#f8fafc',
                    border: '1px dashed rgba(148, 163, 184, 0.36)'
                  }}
                >
                  <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.7 }}>
                    Add a YouTube video ID to generate the partner preview card here.
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 4,
                  backgroundColor: alpha('#2563eb', 0.04),
                  border: '1px solid rgba(37, 99, 235, 0.1)'
                }}
              >
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.7 }}>
                  Tip: keep partner briefs concise and premium in tone. These cards perform best when the first sentence communicates destination value quickly.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddEditPartner;
